// src/components/public/PublicNavbar.jsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Globe, User, Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useAuth from '../../hooks/useAuth';

const PublicNavbar = () => {
  const { isAuthenticated } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showInsuranceDropdown, setShowInsuranceDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  const languages = {
    en: 'English',
    hy: 'Հայերեն',
    ru: 'Русский'
  };

  // Insurance dropdown items
  const insuranceItems = [
    { path: '/InsuranceAM', label: 'insuranceAM' },
    { path: '/InsuranceGE', label: 'insuranceGE' },
    { path: '/InsuranceRU', label: 'insuranceRU' },   
  ];
  
  // Only showing home, insurance, and consultation in public nav
  // No "registrations" item in public nav - that's only accessible via Dashboard
  const menuItems = [
    {
      path: '/',
      label: 'home'
    },
    {
     path: '/insurance',
      label: 'insurance',
      hasDropdown: true,
      dropdownItems: insuranceItems
    },
    {
      path: '/queue-russia',
      label: 'queueToRussia'
    },
    {
      path: '/consultation',
      label: 'technicalConsultation'
    }
  ];

  // Helper function to check if a path is active
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  return (
    <nav className="bg-primary-700 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex-shrink-0 hover:opacity-90 transition-opacity duration-150"
            >
              <span className="text-2xl font-bold tracking-wide bg-white text-primary-700 px-3 py-1 rounded-md">{t('companyNameShort')}</span>
            </Link>
          </div>

          {/* Desktop Navigation Menu */}
          <div className="hidden md:block">
          <div className="flex items-center space-x-4">
              {menuItems.map((item) => (
                item.hasDropdown ? (
                  <div key={item.path} className="relative">
					  {/* This part will be in future */}
				  
					  {/* <button
                      onClick={() => setShowInsuranceDropdown(!showInsuranceDropdown)}
                      className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition duration-150 ${
                        location.pathname.startsWith('/insuranceGE') ? 'bg-primary-800' : 'hover:bg-primary-800'
                      }`}
                     
                    >
                      <span>{t(item.label)}</span>
                      <ChevronDown className="ml-1 h-4 w-4" />
					  </button>  */}
                    
                    {showInsuranceDropdown && (
                      <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          {item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.path}
                              to={dropdownItem.path}
                              onClick={() => setShowInsuranceDropdown(false)}
                              className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                            >
                              {t(dropdownItem.label)}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition duration-150 ${
                      isActive(item.path) ? 'bg-primary-800' : 'hover:bg-primary-800'
                    }`}
                  >
                    <span>{t(item.label)}</span>
                  </Link>
                )
              ))}
            </div>
          </div>

          {/* Right Side Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-primary-800 transition duration-150"
              >
                <Globe className="h-5 w-5" />
                <span className="text-sm font-medium">{languages[language]}</span>
              </button>

              {showLangDropdown && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    {Object.entries(languages).map(([code, name]) => (
                      <button
                        key={code}
                        onClick={() => {
                          setLanguage(code);
                          setShowLangDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Login / Dashboard Button */}
            {isAuthenticated ? (
              <Link 
                to="/dashboard"
                className="flex items-center px-4 py-2 bg-secondary-600 hover:bg-secondary-700 rounded-md transition duration-150 shadow-md"
              >
                <span className="text-sm font-medium">{t('dashboard')}</span>
              </Link>
            ) : (
              <Link 
                to="/login"
                className="flex items-center px-4 py-2 bg-secondary-600 hover:bg-secondary-700 rounded-md transition duration-150 shadow-md"
              >
                <User className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">{t('login')}</span>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-primary-600 focus:outline-none"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-primary-800">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {menuItems.map((item) => (
              item.hasDropdown ? (
                <div key={item.path} className="space-y-1">
				 {/* This part will be in future */}
					 {/*
                  <button
                    onClick={() => setShowInsuranceDropdown(!showInsuranceDropdown)}
                    className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-base font-medium text-white ${
                      location.pathname.startsWith('/insuranceGE') ? 'bg-primary-600' : 'hover:bg-primary-600'
                    }`}
                  >
                    <span>{t(item.label)}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showInsuranceDropdown ? 'transform rotate-180' : ''}`} />
                  </button>
					 */}
                  {showInsuranceDropdown && (
                    <div className="pl-4 space-y-1">
                      {item.dropdownItems.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.path}
                          to={dropdownItem.path}
                          className={`block px-3 py-2 rounded-md text-sm font-medium text-white ${
                            isActive(dropdownItem.path) ? 'bg-primary-500' : 'hover:bg-primary-600'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t(dropdownItem.label)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 rounded-md text-base font-medium text-white ${
                    isActive(item.path) ? 'bg-primary-600' : 'hover:bg-primary-600'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t(item.label)}
                </Link>
              )
            ))}
          </div>
          <div className="pt-4 pb-3 border-t border-primary-600">
            <div className="flex items-center justify-between px-5">
              <div className="text-base font-medium">{languages[language]}</div>
              <div>
                {isAuthenticated ? (
                  <Link 
                    to="/dashboard"
                    className="inline-flex items-center px-4 py-2 bg-secondary-600 rounded-md text-sm font-medium text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('dashboard')}
                  </Link>
                ) : (
                  <Link 
                    to="/login"
                    className="inline-flex items-center px-4 py-2 bg-secondary-600 rounded-md text-sm font-medium text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('login')}
                  </Link>
                )}
              </div>
            </div>
            <div className="mt-3 px-2 space-y-1">
              {Object.entries(languages).map(([code, name]) => (
                <button
                  key={code}
                  onClick={() => {
                    setLanguage(code);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white hover:bg-primary-600"
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default PublicNavbar;